// Scan the source forward in numbered windows until the LLM locates where the
// real screenplay body begins; everything before is planning front matter and is
// dropped before chunking. Returns a 0-based line offset to start from (0 = whole
// file). Robust by construction: a well-formed screenplay hits in the first
// window; a file with no detectable body scans to EOF and falls back to 0 so
// nothing is ever silently discarded.

import { runBodyStart } from "../../llm/tasks/ingest/body-start.js";
import type { LlmTaskExecutor } from "../../llm/types.js";

const WINDOW = 50;
const OVERLAP = 10; // step = WINDOW - OVERLAP, so a 集头/场次 split across a window edge is still seen together

export interface BodyStartScan {
  line: number; // 0-based offset into the source lines to start ingesting from
  scannedLines: number; // how far the scan read before hitting / giving up
  found: boolean;
  error?: string; // set when the scan fell back to 0 because a call threw
}

export async function detectBodyStart(
  executor: LlmTaskExecutor,
  text: string,
  signal?: AbortSignal,
): Promise<BodyStartScan> {
  const lines = text.split("\n");
  const step = WINDOW - OVERLAP;
  try {
    for (let start = 0; start < lines.length; start += step) {
      const end = Math.min(start + WINDOW, lines.length);
      // 1-based line numbers so they match how humans/the model count.
      const numbered = lines.slice(start, end).map((l, i) => `${start + i + 1}\t${l}`).join("\n");
      const res = await runBodyStart(executor, numbered, signal);
      if (res.found && res.line !== null) {
        // Clamp the reported 1-based line into range, convert to 0-based.
        const idx = Math.min(Math.max(res.line - 1, 0), lines.length - 1);
        return { line: idx, scannedLines: end, found: true };
      }
      if (end >= lines.length) break;
    }
  } catch (e) {
    // A failure on this pre-step must never make ingest worse than before: fall
    // back to ingesting the whole file — but record the real error so a silent
    // fallback (front matter NOT stripped) is diagnosable in body_start.json.
    return { line: 0, scannedLines: 0, found: false, error: e instanceof Error ? e.message : String(e) };
  }
  return { line: 0, scannedLines: lines.length, found: false };
}
